import Box from '@mui/material/Box';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import DataTable from '~components/DataTable/DataTable';
import { ClearFiltersLink } from '~components/Filters/ClearFiltersHint';
import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import type { DriverDay as DriverDayT } from '~hooks/useDriverDays/models';
import { useDriverDays } from '~hooks/useDriverDays/useDriverDays';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store/RootStore';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { DriverDayRow, getDriverDayColumns } from './columnDefinitions';
import { DriverDayDetails } from './DriverDayDetails';
import { DriverDayFilters } from './DriverDayFilters/DriverDayFilters';

const transformDriverDayToDriverDayRow = (driverDay: DriverDayT): DriverDayRow => ({
  id: driverDay.id,
  driverDay,
  date: driverDay.driverDayDate,
  driver: driverDay.driver.fullName,
  truckNo: driverDay.equipmentExternalIds[0] ?? null,
  vendor: driverDay.vendorName,
  shiftStart: driverDay.adjustedStartedShiftAt ?? driverDay.startedShiftAt,
  shiftEnd: driverDay.adjustedEndedShiftAt ?? driverDay.endedShiftAt,
  break: driverDay.totalBreakMinutes ?? 0,
  totalHrs: driverDay.totalShiftMinutes ? driverDay.totalShiftMinutes / 60 : 0,
  jobCt: driverDay.jobCount,
  loadCt: driverDay.loadsCount,
  tickets: driverDay.ticketsCount,
  materials: [
    ...new Set(
      driverDay.deliveredQuantities.flatMap(({ materialName }) =>
        materialName ? [materialName] : [],
      ),
    ),
  ].join(', '),
  status: {
    approvable: driverDay.approvable,
    approved: driverDay.approved,
    driverDayId: driverDay.id,
    driverCompanyId: driverDay.driver.company?.id,
    driverId: driverDay.driver.id,
    driverIsShared: Boolean(driverDay.driver.companyShare),
    isOngoingShift: Boolean(driverDay.startedShiftAt && !driverDay.endedShiftAt),
    unapprovable: driverDay.unapprovable,
  },
});

export const DriverDay = observer(() => {
  const [searchQuery, setSearchQuery] = useState('');
  const { driverDayStore, userStore } = useStores();
  const previousCompanyIdRef = useRef<string | undefined>(undefined);
  const {
    fetchDriverDaysByCompanyId,
    subscribeToDriverDayUpdates,
    subscribeToJobUpdates,
    jobSubscription,
  } = useDriverDays();

  const companyId = getEffectiveUserCompanyId(userStore);

  // Render-phase (not an effect) so the restored dates are already in the store
  // when the fetch effect below takes its dependency snapshot — an effect-based
  // restore would fetch once with defaults and immediately refetch with the
  // stored range.
  const hasRestoredDateFiltersRef = useRef(false);
  if (!hasRestoredDateFiltersRef.current) {
    hasRestoredDateFiltersRef.current = true;
    driverDayStore.restoreDateFilters();
  }

  const { goToPage, isFetching, pagination, rows, setRows, startOver } =
    useCursorPagination<DriverDayT>({
      fetchPage: ({ cursor, limit, link, signal }) =>
        fetchDriverDaysByCompanyId({
          companyId,
          cursor,
          limit,
          link,
          searchQuery,
          signal,
          sort: driverDayStore.driverDaySort,
        }),
      limit: DEFAULT_API_PAGE_SIZE,
      onFetchError: (error) =>
        reportRequestError(
          error,
          t('error_messages.driver_days.failed_to_fetch_driver_days'),
        ),
    });

  const upsertDriverDayRow = useCallback(
    (driverDay: DriverDayT) => {
      setRows((driverDays) => {
        const shownDriverDay = driverDays.find(({ id }) => id === driverDay.id);

        if (!shownDriverDay) {
          const { startDate } = driverDayStore.dateFilters;
          const matchesStartDateFilter =
            !startDate ||
            driverDay.driverDayDate.format('YYYY-MM-DD') ===
              startDate.format('YYYY-MM-DD');

          return matchesStartDateFilter ? [driverDay, ...driverDays] : driverDays;
        }

        if (JSON.stringify(shownDriverDay) === JSON.stringify(driverDay)) {
          return driverDays;
        }

        return driverDays.map((row) => (row.id === driverDay.id ? driverDay : row));
      });
    },
    [driverDayStore, setRows],
  );

  const columns = useMemo(
    () => getDriverDayColumns(upsertDriverDayRow),
    [upsertDriverDayRow],
  );

  useEffect(() => {
    // Clear data only when company actually changes
    if (companyId && previousCompanyIdRef.current !== companyId) {
      driverDayStore.clearDriverDayData();
      setSearchQuery('');
    }
    // Update the ref to track the current company ID
    previousCompanyIdRef.current = companyId;

    void startOver();
  }, [
    startOver,
    searchQuery,
    companyId,
    JSON.stringify(driverDayStore.dateFilters),
    JSON.stringify(driverDayStore.filters),
    JSON.stringify(driverDayStore.driverDaySort),
  ]);

  useEffect(() => {
    if (!companyId) {
      return undefined;
    }

    const driverDaySubscription = subscribeToDriverDayUpdates(
      companyId,
      upsertDriverDayRow,
      { triggerResourceUsageLogsRefetch: true },
    );

    return () => {
      driverDaySubscription?.unsubscribe();
    };
  }, [companyId, subscribeToDriverDayUpdates, upsertDriverDayRow]);

  useEffect(() => {
    if (companyId) {
      jobSubscription?.unsubscribe();
      subscribeToJobUpdates(companyId, { triggerResourceUsageLogsRefetch: true });
    }

    return () => {
      jobSubscription?.unsubscribe();
    };
  }, [companyId, subscribeToJobUpdates]);

  const handleClearFiltersKeepSearch = useCallback(() => {
    // The quick-filter search is local state and survives untouched; the fetch
    // effect keys on the stringified store filters, so it refires on its own.
    driverDayStore.setDateFilters(null, null);
    driverDayStore.resetFilters();
  }, [driverDayStore]);

  return (
    <Box>
      <DriverDayFilters />
      <DataTable
        // Remounting resets the expanded detail panels and the search box.
        key={companyId}
        id="driver-day-grid"
        columns={columns}
        rows={rows.map(transformDriverDayToDriverDayRow)}
        loading={isFetching}
        headerTrailingComponent={
          <DataGridPager
            isFetching={isFetching}
            onNavigate={(link) => void goToPage(link)}
            pagination={pagination}
          />
        }
        onSearchChange={setSearchQuery}
        renderDetailPanel={(row) => (
          <DriverDayDetails
            driverDay={row.driverDay}
            onDriverDayChange={upsertDriverDayRow}
          />
        )}
        noRowsOverlayText={
          driverDayStore.hasUnfilteredResults
            ? t('common.no_results_with_filters')
            : undefined
        }
        noRowsOverlayAction={
          driverDayStore.hasUnfilteredResults ? (
            <ClearFiltersLink onClearFilters={handleClearFiltersKeepSearch} />
          ) : undefined
        }
      />
    </Box>
  );
});
